<script setup lang="ts">
import DashboardLayout from '@/onenetwork/layouts/DashboardLayout.vue'
import DataTable from '@/onenetwork/components/datatable/DataTable.vue'
import PushCampaignDetail from '@/modules/push-campaign/views/PushCampaignDetail.vue'
import { Icon } from '@iconify/vue'
import { computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import _ from 'lodash'
import {
  PushCampaignListActionTypes,
  usePushCampaignListStore,
} from '@/modules/push-campaign/stores/push-campaign.list.store.ts'
import { useReactiveQueryParam, useUpdateUrlParam } from '@/composables/useReactiveQueryParam'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import { PushCampaignAPI } from '../constants/push-campaign.api.uri.const'

const pushCampaignListStore = usePushCampaignListStore()
const router = useRouter()

const pageTabs = computed(() => {
  return pushCampaignListStore.tabs
})
const displayTabContent = computed(() => {
  return pushCampaignListStore.tabs?.find((tab) => tab.active)?.id || 'list'
})
const activeTab = computed(() => {
  return pushCampaignListStore.tabs.find((tab) => tab.active)
})
const detailId: any = useReactiveQueryParam('detailId')
const { updateUrlParam } = useUpdateUrlParam()

const TableConfig = computed(() => {
  return pushCampaignListStore.tableConfig
})
const loading = computed(() => {
  return pushCampaignListStore.action_type == PushCampaignListActionTypes.FETCHING
})
const page = computed(() => {
  return pushCampaignListStore.page
})
const pageSize = computed(() => {
  return pushCampaignListStore.pageSize
})
const dataSource = computed<any>(() => pushCampaignListStore.data as any)

const setDetailTab = async () => {
  const id = Number(detailId.value || 0)
  if (!Number.isFinite(id) || id <= 0) return
  try {
    const res: any = await APIClient.get(PushCampaignAPI.campaign.detail.replace(':id', String(id)))
    const dto = res?.data?.data?.data ?? res?.data?.data ?? res?.data
    const title = dto?.title ?? `#${id}`
    pushCampaignListStore.newTab({
      id,
      label: `#${id} - ${title}`,
      active: true,
    })
  } catch (e) {
    // fallback: still open a tab by id so detail can attempt fetching
    pushCampaignListStore.newTab({
      id,
      label: `#${id}`,
      active: true,
    })
  }
}

const handleChangeTab = (tab: any) => {
  pushCampaignListStore.changeTab(tab.id)
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? String(activeTab.value?.id) : null)
}

const handleCloseTab = (tab: any) => {
  pushCampaignListStore.closeTab(tab.id)
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? String(activeTab.value?.id) : null)
}

const handleCloseAllTab = () => {
  pushCampaignListStore.closeAllTab()
  updateUrlParam('detailId', null)
}

const handleOpenCreate = () => {
  router.push({ name: 'push-campaign-list-create' })
}

const handleChangePage = (page: number) => {
  pushCampaignListStore.changePage(page)
}

const handleChangePageSize = (size: number) => {
  pushCampaignListStore.changePageSize(size)
}

const handleSort = (field: string, direction: string) => {
  pushCampaignListStore.toggleSort(field, direction)
}

const requestFetchList = _.debounce(async () => {
  try {
    await pushCampaignListStore.fetchList()
  } catch (e) {}
}, 500)

watch(
  () => pushCampaignListStore.uuid,
  () => {
    requestFetchList()
  },
)

onMounted(() => {
  requestFetchList()
  pushCampaignListStore.changeTab('list')
  if (detailId) setDetailTab()
})
</script>

<template>
  <dashboard-layout
    currentRoute="push-campaign/list"
    page-title="Chiến dịch thông báo đẩy"
    :tabs="pageTabs"
    :tabsEvent="{
      close: handleCloseTab,
      select: handleChangeTab,
      closeAll: handleCloseAllTab,
    }"
  >
    <template v-if="displayTabContent == 'list'">
      <div
        class="flex justify-end mb-4 p-4 bg-white rounded-lg"
        style="
          box-shadow:
            rgba(14, 63, 126, 0.04) 0px 0px 0px 1px,
            rgba(42, 51, 69, 0.04) 0px 1px 1px -0.5px,
            rgba(42, 51, 70, 0.04) 0px 3px 3px -1.5px;
        "
      >
        <button
          class="table-btn-action btn bg-primary bg-hover-primary text-white"
          @click="handleOpenCreate"
        >
          <Icon icon="gridicons:create" height="18px" />
          Thêm chiến dịch mới
        </button>
      </div>
      <data-table
        :loading="loading"
        :tableConfig="TableConfig"
        :page-size="pageSize"
        :page="page"
        :total-page="dataSource?.totalPage || 0"
        :total-records="dataSource?.totalRecords || 0"
        :data="dataSource?.list || []"
        primaryField="id"
        @page-changed="handleChangePage"
        @pagesize-changed="handleChangePageSize"
        @toggle-visibility="pushCampaignListStore.toggleColumnVisibility"
        @on-sort="handleSort"
      />
    </template>
    <template v-else>
      <template v-for="tab in pageTabs">
        <template v-if="tab.id == displayTabContent">
          <panel :key="tab.id">
            <push-campaign-detail :campaign-id="tab.id === 'create' ? null : tab.id" />
          </panel>
        </template>
      </template>
    </template>
  </dashboard-layout>
</template>

<style scoped></style>
